import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown, X } from 'lucide-react';
import React, { useState } from 'react';
import {
  AxisPreferences,
  CurveId,
  CurvePreferences,
  CurveRelation,
} from '../../config/plotTypes';
import { averageColorsInHSLA } from '../../config/relationColors';
import { ColorPicker } from '../CurveSettings/ColorPicker';
import { MainCurveDropdown } from './MainCurveDropdown';
import { RangeSliderToggle } from './RangeSliderToggle';
import { RelationTypeDropdown } from './RelationTypeDropdown';

const getFirstDistinctCurve = (
  curveKeys: CurveId[],
  excludedCurveId?: CurveId
): CurveId | undefined =>
  curveKeys.find((curveId) => curveId !== excludedCurveId);

const resolveSecondCurve = (
  curveKeys: CurveId[],
  mainCurve: CurveId,
  currentSecondCurve?: CurveId
): CurveId | undefined => {
  if (currentSecondCurve && currentSecondCurve !== mainCurve) {
    return currentSecondCurve;
  }

  return getFirstDistinctCurve(curveKeys, mainCurve);
};

const getBlendedRelationColor = (
  curves: Record<CurveId, CurvePreferences>,
  mainCurve: CurveId,
  secondCurve?: CurveId,
  fallbackAlpha = 0.35
): string => {
  const curveColors = [
    curves[mainCurve]?.color,
    secondCurve && curves[secondCurve]?.color,
  ].filter((color): color is string => Boolean(color));

  return averageColorsInHSLA(curveColors, fallbackAlpha);
};

export interface RelationsGlobalSettingsProps {
  curves: Record<string, CurvePreferences>;
  axes: Record<string, AxisPreferences>;
  relations: CurveRelation[];
  onUpdateRelation: (index: number, relation: CurveRelation) => void;
  onAddRelation?: () => void;
  onDeleteRelation?: (index: number) => void;
  showRangeSlider: boolean;
  onToggleRangeSlider: () => void;
  showRelationTypeSelector?: boolean;
  useRangeSliderToggle?: boolean;
}

export const RelationsGlobalSettings: React.FC<
  RelationsGlobalSettingsProps
> = ({
  curves,
  relations,
  onUpdateRelation,
  onAddRelation,
  onDeleteRelation,
  showRangeSlider,
  onToggleRangeSlider,
  showRelationTypeSelector = true,
  useRangeSliderToggle = true,
}) => {
  const [collapsedKeys, setCollapsedKeys] = useState<Set<number>>(new Set());

  const relationCurveKeys = Object.keys(curves) as CurveId[];

  const toggleCollapsed = (index: number) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: '0.9rem', fontWeight: 700 }}
        >
          Relations
        </Typography>
        <Button size="small" variant="text" onClick={() => onAddRelation?.()}>
          + Ajouter
        </Button>
      </Stack>
      {relations.map((relation, index) =>
        (() => {
          const isExpanded = !collapsedKeys.has(index);
          const isPairRelation =
            relation.relationType === 'correlation' ||
            relation.relationType === 'difference';
          const mainCurve = relation.mainCurve as CurveId;
          const secondCurve = resolveSecondCurve(
            relationCurveKeys,
            mainCurve,
            relation.relatedCurves[0] as CurveId | undefined
          );
          const resolvedRelationColor =
            relation.color ||
            (isPairRelation
              ? getBlendedRelationColor(curves, mainCurve, secondCurve)
              : 'rgba(0, 0, 0, 0.35)');

          return (
            <Box
              key={`${relation.mainCurve}-${index}`}
              sx={{
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              {/* Card header */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                px={1.5}
                py={0.75}
              >
                <IconButton
                  size="small"
                  onClick={() => toggleCollapsed(index)}
                  sx={{
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                  aria-label="Développer la relation"
                >
                  <ChevronDown size={14} />
                </IconButton>
                <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                  {relation.displayName ||
                    `${relation.relationType} \u2014 ${relation.mainCurve}`}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onDeleteRelation?.(index)}
                  aria-label="Supprimer la relation"
                >
                  <X size={14} />
                </IconButton>
              </Stack>
              <Collapse in={isExpanded}>
                <Divider />
                <Box
                  px={1.5}
                  py={1}
                  display="flex"
                  flexDirection="column"
                  gap={1.5}
                >
                  {showRelationTypeSelector && (
                    <Box>
                      <Typography variant="caption" display="block" mb={0.5}>
                        Type de relation
                      </Typography>
                      <RelationTypeDropdown
                        value={relation.relationType}
                        onChange={(value) =>
                          onUpdateRelation(index, {
                            ...relation,
                            relationType: value,
                            relatedCurves:
                              value === 'interval'
                                ? relation.relatedCurves.slice(0, 2)
                                : relation.relatedCurves.slice(0, 1),
                            color:
                              value === 'correlation' || value === 'difference'
                                ? getBlendedRelationColor(
                                    curves,
                                    relation.mainCurve as CurveId,
                                    relation.relatedCurves[0] as
                                      | CurveId
                                      | undefined
                                  )
                                : relation.color,
                          })
                        }
                      />
                    </Box>
                  )}
                  {relation.relationType === 'interval' ? (
                    <>
                      <Box>
                        <Typography variant="caption" display="block" mb={0.5}>
                          Courbe de moyenne
                        </Typography>
                        <MainCurveDropdown
                          value={relation.mainCurve}
                          curves={curves}
                          onChange={(value) =>
                            onUpdateRelation(index, {
                              ...relation,
                              mainCurve: value,
                            })
                          }
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" mb={0.5}>
                          Courbe min
                        </Typography>
                        <Select
                          value={relation.relatedCurves[0] ?? ''}
                          onChange={(e) => {
                            const minCurve = e.target.value as string;
                            const maxCurve = relation.relatedCurves[1] ?? '';
                            onUpdateRelation(index, {
                              ...relation,
                              relatedCurves: [minCurve, maxCurve].filter(
                                Boolean
                              ),
                            });
                          }}
                          size="small"
                          fullWidth
                        >
                          {relationCurveKeys.map((curveKey) => (
                            <MenuItem key={curveKey} value={curveKey}>
                              {curves[curveKey].displayName || curveKey}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" mb={0.5}>
                          Courbe max
                        </Typography>
                        <Select
                          value={relation.relatedCurves[1] ?? ''}
                          onChange={(e) => {
                            const minCurve = relation.relatedCurves[0] ?? '';
                            const maxCurve = e.target.value as string;
                            onUpdateRelation(index, {
                              ...relation,
                              relatedCurves: [minCurve, maxCurve].filter(
                                Boolean
                              ),
                            });
                          }}
                          size="small"
                          fullWidth
                        >
                          {relationCurveKeys.map((curveKey) => (
                            <MenuItem key={curveKey} value={curveKey}>
                              {curves[curveKey].displayName || curveKey}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box>
                        <Typography variant="caption" display="block" mb={0.5}>
                          {relation.relationType === 'difference'
                            ? 'Courbe de référence'
                            : 'Première courbe'}
                        </Typography>
                        <MainCurveDropdown
                          value={relation.mainCurve}
                          curves={curves}
                          onChange={(value) =>
                            (() => {
                              const nextMainCurve = value as CurveId;
                              const nextSecondCurve = resolveSecondCurve(
                                relationCurveKeys,
                                nextMainCurve,
                                relation.relatedCurves[0] as CurveId | undefined
                              );

                              onUpdateRelation(index, {
                                ...relation,
                                mainCurve: nextMainCurve,
                                relatedCurves: nextSecondCurve
                                  ? [nextSecondCurve]
                                  : [],
                                color: getBlendedRelationColor(
                                  curves,
                                  nextMainCurve,
                                  nextSecondCurve
                                ),
                              });
                            })()
                          }
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" mb={0.5}>
                          {relation.relationType === 'difference'
                            ? 'Courbe à comparer'
                            : 'Deuxième courbe'}
                        </Typography>
                        <MainCurveDropdown
                          value={secondCurve ?? ''}
                          curves={curves}
                          onChange={(value) =>
                            (() => {
                              const selectedSecondCurve = value as CurveId;
                              const nextSecondCurve =
                                selectedSecondCurve ===
                                (relation.mainCurve as CurveId)
                                  ? resolveSecondCurve(
                                      relationCurveKeys,
                                      relation.mainCurve as CurveId,
                                      undefined
                                    )
                                  : selectedSecondCurve;

                              onUpdateRelation(index, {
                                ...relation,
                                relatedCurves: nextSecondCurve
                                  ? [nextSecondCurve]
                                  : [],
                                color: getBlendedRelationColor(
                                  curves,
                                  relation.mainCurve as CurveId,
                                  nextSecondCurve
                                ),
                              });
                            })()
                          }
                        />
                      </Box>
                    </>
                  )}
                  <Box>
                    <Typography variant="caption" display="block" mb={0.5}>
                      Couleur de superposition
                    </Typography>
                    <ColorPicker
                      value={resolvedRelationColor}
                      enableAlpha
                      onChange={(value) =>
                        onUpdateRelation(index, { ...relation, color: value })
                      }
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })()
      )}

      {useRangeSliderToggle && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Typography
            variant="subtitle2"
            sx={{ fontSize: '0.9rem', fontWeight: 700 }}
            mb={1}
          >
            Paramètres globaux
          </Typography>
          <Box
            sx={{
              mb: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box px={1.5} py={1}>
              <RangeSliderToggle
                show={showRangeSlider}
                onToggle={onToggleRangeSlider}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
